import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

// Sound and TTS for web
const speakToken = (tokenNumber) => {
  if ('speechSynthesis' in window) {
    const utterance = new SpeechSynthesisUtterance(`Token Number ${tokenNumber}`);
    utterance.rate = 0.6;
    speechSynthesis.speak(utterance);
  }
};

const playSound = () => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.frequency.value = 1200;
  oscillator.type = 'sine';
  
  gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.1);
};

function ViewMode() {
  const navigate = useNavigate();
  const [dashData, setDashData] = useState({
    docLogo: 'https://tokenly.biswaas.com/assets/img/app-icon.png',
    appLogo: 'https://tokenly.biswaas.com/assets/img/app-icon.png',
    leftAd: '',
    rightAd: '',
    offDays: '',
    remarkText: '',
    clinicName: 'Tokenly',
    is_clinic: 0,
    hours_detail: {
      is_closed: 0,
      time: []
    }
  });

  const [tokenData, setTokenData] = useState({
    lastToken: 'N/A',
    currentToken: 'N/A',
    isRecall: 0,
  });

  const [errorMsg, setErrorMsg] = useState('');
  const [currentToken, setCurrentToken] = useState(0);
  const [mobileNo, setMobileNo] = useState('');
  const [isDoc, setDoc] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const [isRecall, setIsRecall] = useState(0);
  const [imageHeight1, setImageHeight1] = useState(0);
  const [imageHeight2, setImageHeight2] = useState(0);

  useEffect(() => {
    getData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dashData.leftAd) {
      const img = new Image();
      img.onload = () => {
        const ratio = img.height / img.width;
        setImageHeight1(250 * ratio);
      };
      img.src = dashData.leftAd;
    }
    if (dashData.rightAd) {
      const img = new Image();
      img.onload = () => {
        const ratio = img.height / img.width;
        setImageHeight2(250 * ratio);
      };
      img.src = dashData.rightAd;
    }
  }, [dashData]);

  const getData = async () => {
    try {
      // Logged-in doctor/staff: always show their own view
      const user = JSON.parse(localStorage.getItem('tokenly_user') || '{}');
      if (user.phone) {
        setMobileNo(user.phone);
        fetchData(user.phone);
        return;
      }

      const myMobileNo = localStorage.getItem('myMobileNo');
      const isLogin = localStorage.getItem('isLogin');
      if (myMobileNo && myMobileNo !== '') {
        setMobileNo(myMobileNo);
        if (isLogin === 'yes') {
          fetchData(myMobileNo);
        }
      }
    } catch (e) {
      console.error('Error reading data:', e);
    }
  };

  const fetchData = async (mobileNo) => {
    try {
      const resp = await fetch(
        'https://tokenly.biswaas.com/api/doctorViewApi/' + mobileNo,
      );
      const data = await resp.json();

      if (data.status) {
        setDoc(true);
        setDashData(data.data);
        getTokenData(mobileNo, isRecall, currentToken);
        localStorage.setItem('myMobileNo', mobileNo);
        localStorage.setItem('isLogin', 'yes');
      } else {
        setDoc(false);
        setErrorMsg(data.message);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const getTokenData = async (mobileNo, isRecall, currentToken) => {
    try {
      const resp = await fetch(
        'https://tokenly.biswaas.com/api/doctorTokenApi/' + mobileNo,
      );
      const data = await resp.json();

      if (data.status) {
        setDoc(true);

        const doctors = data.data.staffDoctorsList || [];
        if (doctors.length === 0) {
          setLoading(false);
          return;
        }

        // Show only the logged-in doctor; fall back to the latest updated one
        const user = JSON.parse(localStorage.getItem('tokenly_user') || '{}');
        const isLoggedInDoctor = item =>
          [user.doctorId, user.userId].some(
            id => id != null && String(id) === String(item.doctorId),
          );
        const currentTokenData =
          doctors.find(isLoggedInDoctor) ||
          doctors.reduce((latest, current) => {
            return new Date(current.updated_at) > new Date(latest.updated_at)
              ? current
              : latest;
          });

        if (currentTokenData.isRecall !== isRecall) {
          speakToken(currentTokenData.currentToken);
          setIsRecall(currentTokenData.isRecall);
        }

        setTokenData(currentTokenData);

        if (currentTokenData.currentToken !== currentToken) {
          setCurrentToken(currentTokenData.currentToken);

          if (currentToken !== 0 && currentTokenData.currentToken !== 'N/A') {
            speakToken(currentTokenData.currentToken);
          }
        }
      } else {
        setDoc(false);
        setErrorMsg(data.message);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching token data:', error);
      setLoading(false);
    }
  };

  const truncateText = text => {
    return text.length > 20 ? text.substring(0, 20) + '...' : text;
  };

  const handleLogout = () => {
    localStorage.removeItem('tokenly_token');
    localStorage.removeItem('tokenly_user');
    localStorage.removeItem('myMobileNo');
    localStorage.removeItem('isLogin');
    navigate('/login');
  };

  const handleSubmit = () => {
    setLoading(true);
    if (mobileNo === '') {
      setErrorMsg('Please Enter Mobile Number');
      setLoading(false);
    } else {
      fetchData(mobileNo);
    }
  };

  useEffect(() => {
    const intervalCall2 = setInterval(() => {
      if (mobileNo) {
        fetchData(mobileNo);
      }
    }, 3600000);
    return () => {
      clearInterval(intervalCall2);
    };
  }, [mobileNo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const intervalCall = setInterval(() => {
      if (
        isDoc &&
        dashData.is_clinic === 0 &&
        dashData.hours_detail &&
        dashData.hours_detail.is_closed === 0
      ) {
        const hours_detail = dashData.hours_detail.time;

        hours_detail.forEach((hd) => {
          const startTime = hd.start_time;
          const endTime = hd.end_time;
          const cdt = moment(startTime, 'HH:mm A')
            .utcOffset('+05:30')
            .subtract(30, 'minutes');
          const cdt2 = moment(endTime, 'HH:mm A').add(240, 'minutes');
          const now = moment();

          if (now.isAfter(cdt) && now.isBefore(cdt2)) {
            getTokenData(mobileNo, isRecall, currentToken);
          }
        });
      } else if (isDoc && dashData.is_clinic === 1) {
        getTokenData(mobileNo, isRecall, currentToken);
      }
    }, 5000);
    return () => {
      clearInterval(intervalCall);
    };
  }, [isDoc, mobileNo, isRecall, currentToken, dashData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const intervalCall3 = setInterval(() => {
      const date = moment()
        .utcOffset('+05:30')
        .format('dddd, DD MMMM YYYY  hh:mm:ss A');
      setCurrentDate(date);
    }, 1000);
    return () => {
      clearInterval(intervalCall3);
    };
  }, []);

  return (
    <div className="min-vh-100 bg-white">
      {/* Header */}
      <header className="bg-primary text-white p-3">
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-auto">
              {dashData.docLogo && (
                <img
                  src={dashData.docLogo}
                  alt="Doctor Logo"
                  className="rounded-circle"
                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                />
              )}
            </div>
            <div className="col text-center">
              <h1 className="h2 mb-0 fw-bold">{dashData.clinicName}</h1>
              <p className="mb-0 mt-1">{currentDate}</p>
              <p className="mb-0">Tokenly 2.2</p>
            </div>
            <div className="col-auto">
              {dashData.appLogo ? (
                <img
                  src={dashData.appLogo}
                  alt="App Logo"
                  className="rounded-circle"
                  style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                />
              ) : (
                <span className="badge bg-secondary">Tokenly 2.2</span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {isDoc ? (
          <div className="container-fluid p-0">
            <div className="row g-0">
              {/* Left Advertisement */}
              <div className="col-lg-2 col-md-3 d-none d-md-block">
                {dashData.leftAd && (
                  <img
                    src={dashData.leftAd}
                    alt="Left Advertisement"
                    className="img-fluid h-100"
                    style={{ objectFit: 'contain', maxHeight: 'calc(100vh - 120px)' }}
                  />
                )}
              </div>

              {/* Center Content */}
              <div className="col-lg-8 col-md-6 col-12 bg-light">
                <div className="container py-2">
                  <div className="text-center mb-2">
                    <h2 className="h3 fw-bold text-dark mb-2">Tokenly - Token Management System</h2>
                    <p className="lead text-muted">Please Visit with token number</p>
                  </div>

                  {/* Token Display */}
                  <div className="row justify-content-center mb-2">
                    <div className="col-lg-8 col-md-10">
                      <div className="text-center p-2">
                        {/* Doctor Name */}
                        {tokenData.fullname && tokenData.fullname !== '' && (
                          <div 
                            className="mb-2 cursor-pointer" 
                            onClick={() => speakToken(currentToken)}
                            style={{ cursor: 'pointer' }}
                          >
                            <h3 className="h4 text-primary fw-bold mb-1">
                              {truncateText(tokenData.fullname)}
                            </h3>
                          </div>
                        )}
                        
                        {/* Room Number */}
                        {tokenData.room_num && tokenData.room_num !== '' && (
                          <div className="mb-2">
                            <span className="badge bg-secondary fs-6">
                              <i className="bi bi-door-open me-1"></i>
                              Room: {tokenData.room_num}
                            </span>
                          </div>
                        )}

                        {/* Main Token Circle */}
                        <div className="d-flex justify-content-center my-2">
                          <div 
                            className="rounded-circle bg-primary d-flex align-items-center justify-content-center cursor-pointer"
                            style={{ width: '300px', height: '300px', cursor: 'pointer' }}
                            onClick={() => speakToken(currentToken)}
                          >
                            <span className="text-white fw-bold" style={{ fontSize: '6rem' }}>
                              {tokenData.currentToken !== null ? tokenData.currentToken : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="row justify-content-center">
                    <div className="col-lg-8 col-md-10">
                      <div className="text-center">
                        {dashData.remarkText && (
                          <div className="alert alert-warning mb-1 py-1">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {dashData.remarkText}
                          </div>
                        )}
                        {dashData.offDays && (
                          <div className="alert alert-info mb-0 py-1">
                            <i className="bi bi-calendar-x me-2"></i>
                            {dashData.offDays}
                          </div>
                        )}
                        <button
                          onClick={() => navigate(-1)}
                          className="btn btn-primary btn-lg mt-3"
                          style={{ borderRadius: '25px' }}
                        >
                          <i className="bi bi-arrow-left me-2"></i> Back
                        </button>
                        <button
                          onClick={handleLogout}
                          className="btn btn-danger btn-lg mt-3 ms-2"
                          style={{ borderRadius: '25px' }}
                        >
                          <i className="bi bi-box-arrow-right me-2"></i> Logout
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Advertisement */}
              <div className="col-lg-2 col-md-3 d-none d-md-block">
                {dashData.rightAd && (
                  <img
                    src={dashData.rightAd}
                    alt="Right Advertisement"
                    className="img-fluid h-100"
                    style={{ objectFit: 'contain', maxHeight: 'calc(100vh - 120px)' }}
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-center bg-light" style={{ minHeight: 'calc(100vh - 120px)' }}>
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-6 col-md-8 col-12">
                  <div className="card shadow">
                    <div className="card-header bg-primary text-white">
                      <h4 className="mb-0">Login to Tokenly</h4>
                    </div>
                    <div className="card-body p-4">
                      {errorMsg && (
                        <div className="alert alert-danger mb-3">
                          <i className="bi bi-exclamation-circle me-2"></i>
                          {errorMsg}
                        </div>
                      )}
                      
                      <p className="text-muted text-center mb-4">Please Enter Mobile Number</p>
                      
                      <div className="row">
                        <div className="col-12">
                          <div className="input-group mb-3">
                            <span className="input-group-text">
                              <i className="bi bi-telephone"></i>
                            </span>
                            <input
                              type="tel"
                              className="form-control form-control-lg"
                              placeholder="Mobile Number"
                              value={mobileNo}
                              onChange={(e) => setMobileNo(e.target.value)}
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="col-12">
                          {loading ? (
                            <button className="btn btn-primary btn-lg w-100" disabled>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Please Wait...
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary btn-lg w-100"
                              onClick={handleSubmit}
                            >
                              <i className="bi bi-box-arrow-in-right me-2"></i>
                              Login
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default ViewMode;